// ---------------------------------------------------------------------------
// SAVED SEARCHES / JOB ALERTS — single source of truth for the client side.
// ---------------------------------------------------------------------------
// A saved search is a contractor's standing filter set. The hourly cron
// (edge function `job-alerts-digest`) re-runs each ACTIVE search against
// projects published since that search last sent, and emails a digest through
// `send-transactional-email` (template `job_alert_digest`, one-click unsubscribe).
//
// Rules this module encodes:
//   * Filters mirror the browse filters (trade, state, city, residential /
//     commercial, minimum budget) PLUS Phase 2 geography (ZIP + 25/50/100 mi
//     radius or Statewide, and optional service-area cities). Geography rules
//     live in src/lib/geoAlerts.ts and are enforced server-side.
//   * geo_follows_profile=true means the alert copies the contractor's
//     account-level service area (Profile Settings) via a DB trigger. Alerts
//     auto-created from the profile follow it by default.
//   * min_budget: a project with NO budget on file is still delivered.
//   * Rows are never hard-deleted by the unsubscribe link; it only flips
//     `active` to false.
// ---------------------------------------------------------------------------
import { supabase } from '@/lib/supabase';
import type { Lang } from '@/data/constants';
import { GeoSettings, RadiusMiles, describeGeo, normalizeZip, parseCities, previewGeoMatches } from '@/lib/geoAlerts';

export interface SavedSearch {
  id: string;
  user_id: string;
  email: string;
  name: string;
  trades: string[];
  state: string | null;
  city: string | null;
  project_type: 'residential' | 'commercial' | null;
  min_budget: number | null;
  language: string;
  frequency: 'daily' | 'weekly';
  active: boolean;
  unsubscribe_token: string;
  last_sent_at: string | null;
  last_match_count: number;
  total_sent: number;
  created_at: string;
  source?: string;
  zip: string | null;
  radius_miles: RadiusMiles;
  service_cities: string[];
  geo_follows_profile: boolean;
}

/** The editable part of a saved search (everything the form owns). */
export interface SavedSearchDraft extends GeoSettings {
  name: string;
  trades: string[];
  state: string | null;
  city: string | null;
  project_type: 'residential' | 'commercial' | null;
  min_budget: number | null;
  frequency: 'daily' | 'weekly';
  geo_follows_profile: boolean;
}

export const EMPTY_DRAFT: SavedSearchDraft = {
  name: '',
  trades: [],
  state: null,
  city: null,
  project_type: null,
  min_budget: null,
  frequency: 'daily',
  zip: null,
  radius_miles: null,
  service_cities: [],
  geo_follows_profile: false,
};

/** Hard ceiling so one account cannot turn the digest into a mail blast. */
export const MAX_SAVED_SEARCHES = 5;

/** Minimum-budget steps (whole dollars), aligned with BUDGET_BANDS floors. */
export const MIN_BUDGET_STEPS = [5000, 15000, 30000, 60000, 100000];

export const listSavedSearches = async (userId?: string | null): Promise<SavedSearch[]> => {
  if (!userId) return [];
  const { data } = await supabase
    .from('mkt_saved_searches')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: true });
  return ((data || []) as SavedSearch[]).map((r) => ({ ...r, service_cities: r.service_cities || [] }));
};

/** Validation shared by create and edit. Returns a bilingual message or null. */
export const validateDraft = (d: SavedSearchDraft, lang: Lang): string | null => {
  if (!d.name.trim()) {
    return lang === 'es' ? 'Ponle un nombre a la alerta.' : 'Give the alert a name.';
  }
  const hasGeo = !!d.zip || d.service_cities.length > 0;
  if (d.trades.length === 0 && !d.state && !d.city && !d.project_type && d.min_budget == null && !hasGeo && !d.geo_follows_profile) {
    return lang === 'es'
      ? 'Elige al menos un filtro; una alerta sin filtros te enviaría todos los proyectos.'
      : 'Choose at least one filter — an alert with no filters would email you every project.';
  }
  if (d.min_budget != null && d.min_budget < 0) {
    return lang === 'es' ? 'El presupuesto mínimo no puede ser negativo.' : 'Minimum budget cannot be negative.';
  }
  if (!d.geo_follows_profile && d.radius_miles != null && !normalizeZip(d.zip)) {
    return lang === 'es' ? 'Ingresa un código postal de 5 dígitos para usar la distancia.' : 'Enter a 5-digit ZIP code to use a radius.';
  }
  return null;
};

const geoPatch = (d: Pick<SavedSearchDraft, 'zip' | 'radius_miles' | 'service_cities' | 'geo_follows_profile'>) =>
  d.geo_follows_profile
    ? { geo_follows_profile: true } // the DB trigger / profile propagation fills the geo columns
    : {
        geo_follows_profile: false,
        zip: normalizeZip(d.zip),
        radius_miles: d.radius_miles,
        service_cities: parseCities(d.service_cities),
      };

export const createSavedSearch = async (userId: string, email: string, language: Lang, d: SavedSearchDraft) => {
  const { error } = await supabase.from('mkt_saved_searches').insert({
    user_id: userId,
    email,
    language,
    name: d.name.trim(),
    trades: d.trades,
    state: d.state || null,
    city: d.city?.trim() || null,
    project_type: d.project_type,
    min_budget: d.min_budget,
    frequency: d.frequency,
    ...geoPatch(d),
  });
  return error;
};

export const updateSavedSearch = async (id: string, d: Partial<SavedSearchDraft> & { active?: boolean }) => {
  const patch: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (d.name !== undefined) patch.name = d.name.trim();
  if (d.trades !== undefined) patch.trades = d.trades;
  if (d.state !== undefined) patch.state = d.state || null;
  if (d.city !== undefined) patch.city = d.city?.trim() || null;
  if (d.project_type !== undefined) patch.project_type = d.project_type;
  if (d.min_budget !== undefined) patch.min_budget = d.min_budget;
  if (d.frequency !== undefined) patch.frequency = d.frequency;
  if (d.active !== undefined) patch.active = d.active;
  const { error } = await supabase.from('mkt_saved_searches').update(patch).eq('id', id);
  return error;
};

/**
 * Change an alert's geography. When following the profile, the current
 * profile values are copied in right away (the trigger keeps them in sync on
 * later profile edits).
 */
export const updateSavedSearchGeo = async (
  id: string,
  g: GeoSettings & { geo_follows_profile: boolean },
  profileGeo?: GeoSettings
) => {
  const base = g.geo_follows_profile && profileGeo ? { ...profileGeo, geo_follows_profile: false } : g;
  const p = geoPatch(base);
  const { error } = await supabase
    .from('mkt_saved_searches')
    .update({ ...p, geo_follows_profile: g.geo_follows_profile, updated_at: new Date().toISOString() })
    .eq('id', id);
  return error;
};

export const deleteSavedSearch = async (id: string) => {
  const { error } = await supabase.from('mkt_saved_searches').delete().eq('id', id);
  return error;
};

/**
 * How many projects CURRENTLY on the marketplace this filter set would match.
 * Computed server-side (mkt_alert_geo_preview) with the exact digest rules,
 * including radius/city geography. Returns a count only — never locations.
 */
export const previewMatchCount = async (d: SavedSearchDraft, profileGeo?: GeoSettings): Promise<number> => {
  const geo = d.geo_follows_profile && profileGeo ? profileGeo : d;
  const res = await previewGeoMatches({
    trades: d.trades, state: d.state, city: d.city, project_type: d.project_type, min_budget: d.min_budget,
    zip: geo.zip, radius_miles: geo.radius_miles, service_cities: geo.service_cities,
  });
  return res?.count ?? 0;
};

/** Human summary, e.g. "Roofing, HVAC · MO · Within 50 mi of 63101 · $15,000+". */
export const describeSearch = (
  s: Pick<SavedSearch, 'trades' | 'state' | 'city' | 'project_type' | 'min_budget'> & Partial<GeoSettings>,
  lang: Lang,
  tradeName: (slug: string) => string
): string => {
  const parts: string[] = [];
  if (s.trades?.length) parts.push(s.trades.map(tradeName).join(', '));
  const place = [s.city, s.state].filter(Boolean).join(', ');
  if (place) parts.push(place);
  parts.push(describeGeo({ zip: s.zip ?? null, radius_miles: s.radius_miles ?? null, service_cities: s.service_cities || [] }, lang));
  if (s.project_type)
    parts.push(
      s.project_type === 'commercial'
        ? lang === 'es' ? 'Comercial' : 'Commercial'
        : lang === 'es' ? 'Residencial' : 'Residential'
    );
  if (s.min_budget != null) parts.push(`$${s.min_budget.toLocaleString('en-US')}+`);
  return parts.join(' · ');
};

// NOTE: the one-click unsubscribe URL is built SERVER-SIDE by job-alerts-digest.
